import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { companyInfo } from '../data/mock';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { cartCount } = useCart();
  const { user, isAuthenticated } = useAuth();

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  // Handle navigation with scroll to top
  const handleNavClick = (e, path) => {
    e.preventDefault();
    scrollToTop();
    navigate(path);
  };

  // Handle logo click - always go to home and scroll to top
  const handleLogoClick = (e) => {
    e.preventDefault();
    scrollToTop();
    navigate('/');
  };

  const isActive = (path) => {
    // Check if current path starts with the nav link path (for category pages)
    if (path !== '/' && location.pathname.startsWith(path)) {
      return true;
    }
    return location.pathname === path;
  };

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.bagger'), path: '/bagger' },
    // { name: t('nav.radlader'), path: '/radlader' }, // Ausgeblendet
    { name: t('nav.gabelstapler'), path: '/gabelstapler' },
    { name: t('nav.ruettelplatten'), path: '/ruettelplatten' },
    { name: t('nav.stampfer'), path: '/stampfer' },
    { name: t('nav.zubehoer'), path: '/zubehoer' },
    { name: t('nav.vorbestellen'), path: '/vorbestellen' },
    { name: t('nav.ueberUns'), path: '/ueber-uns' },
    { name: t('nav.kontakt'), path: '/kontakt' },
  ];

  const whatsappUrl = `https://wa.me/49${companyInfo.mobile.replace(/\s/g, '').replace(/^0/, '')}?text=Hallo%20Jannesmann%20Baumaschinen%2C%20ich%20habe%20eine%20Frage.`;

  return (
    <header className="sticky top-0 z-50 bg-white">
      {/* Container für Desktop-Bündigkeit - breiterer Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
        {/* Obere weiße Leiste */}
        <div className="flex items-center justify-between h-20 sm:h-24">
          
          {/* Links: Bagger + Schriftzug als kompakter Logo-Block */}
          <a href="/" onClick={handleLogoClick} className="flex items-center gap-1 flex-shrink-0 cursor-pointer">
            {/* Freigestellter Bagger (PNG) - ganz links ohne Abstand */}
            <img
              src="https://customer-assets.emergentagent.com/job_jmbau-shop/artifacts/jnxqoa6v_5FA26D00-C584-48FA-AED8-577FBFE390F0.png"
              alt="Jannesmann Bagger"
              className="h-12 sm:h-18 md:h-20 w-auto object-contain"
            />
            {/* Schriftzug eng daneben - Corporate Design - Mobile etwas kleiner */}
            <div className="flex flex-col leading-none">
              <span className="text-base sm:text-2xl md:text-3xl font-black text-gray-800 tracking-tight">JANNESMANN</span>
              <span className="text-[10px] sm:text-base md:text-xl font-bold text-red-600 tracking-widest">BAUMASCHINEN</span>
            </div>
          </a>

          {/* Sprachumschalter - zwischen Logo und Kontakt-Infos */}
          <div className="flex items-center">
            <LanguageSwitcher />
          </div>

          {/* Mitte: Kontakt-Infos - nur auf Desktop sichtbar */}
          <div className="hidden lg:flex items-center space-x-8">
            {/* WhatsApp */}
            <a 
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center hover:opacity-80 transition-opacity"
            >
              <svg className="w-10 h-10 mr-3 text-red-600" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              <span className="text-xl font-bold text-gray-800">{t('header.whatsapp')}</span>
            </a>

            {/* Trennstrich */}
            <div className="h-12 w-px bg-gray-300"></div>

            {/* Telefon */}
            <a 
              href="tel:+4920419877499"
              className="flex items-center hover:opacity-80 transition-opacity"
            >
              <svg className="w-10 h-10 mr-3 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              <div className="flex flex-col leading-tight">
                <span className="text-lg font-bold text-gray-800">{t('header.openingHours')}</span>
                <span className="text-lg font-bold text-red-600">+49 2041 9877499</span>
              </div>
            </a>

            {/* Trennstrich */}
            <div className="h-12 w-px bg-gray-300"></div>

            {/* E-Mail */}
            <a 
              href="mailto:info@jannesmann.de"
              className="flex items-center hover:opacity-80 transition-opacity"
            >
              <svg className="w-10 h-10 mr-3 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
              <span className="text-xl font-bold text-gray-800">info@jannesmann.de</span>
            </a>
          </div>

          {/* Rechts: Icons */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* WhatsApp-Icon - Mobile: gleicher Stil wie Desktop (rotes Icon ohne Button-Hintergrund) */}
            <a 
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="lg:hidden flex items-center justify-center hover:opacity-80 transition-opacity"
              data-testid="header-whatsapp"
            >
              <svg className="w-8 h-8 sm:w-10 sm:h-10 text-red-600" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </a>

            {/* Text "Anmelden | Registrieren" oder "Mein Konto" */}
            <Link
              to={isAuthenticated ? "/mein-konto" : "/anmelden"}
              className="hidden lg:block text-lg font-bold text-gray-800 hover:text-red-600 transition-colors whitespace-nowrap"
              data-testid="header-login-text"
            >
              {isAuthenticated ? t('header.myAccount') : t('header.login')}
            </Link>

            {/* Trennstrich - nur Desktop */}
            <div className="hidden lg:block h-12 w-px bg-gray-300"></div>

            {/* Benutzer-Icon - gleiche Größe und Farbe wie andere Icons */}
            <Link
              to={isAuthenticated ? "/mein-konto" : "/anmelden"}
              className="flex items-center hover:opacity-80 transition-opacity"
              data-testid="header-user-icon"
            >
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
              ) : (
                <User className="w-10 h-10 text-red-600" strokeWidth={1.5} />
              )}
            </Link>

            {/* Warenkorb-Icon - gleiche Größe und Farbe wie andere Icons */}
            <Link
              to="/warenkorb"
              className="relative flex items-center hover:opacity-80 transition-opacity"
              data-testid="header-cart-icon"
            >
              <ShoppingCart className="w-10 h-10 text-red-600" strokeWidth={1.5} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {cartCount > 9 ? '9+' : cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>

        {/* Mobile Kontakt-Infos - nur auf Mobile sichtbar, über der roten Leiste */}
        <div className="lg:hidden bg-gray-50 border-t border-gray-200 py-1.5 px-2">
          <div className="flex items-center justify-center flex-wrap gap-x-2 gap-y-1 text-[10px] sm:text-xs">
            {/* Telefon mit Öffnungszeiten */}
            <a 
              href="tel:+4920419877499"
              className="flex items-center text-gray-700 hover:text-red-600 transition-colors"
            >
              <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              <span className="font-medium">Mo–Fr 8–18</span>
            </a>
            
            <span className="text-gray-300">|</span>
            
            {/* Telefonnummer */}
            <a 
              href="tel:+4920419877499"
              className="font-bold text-red-600 hover:text-red-700 transition-colors"
            >
              +49 2041 9877499
            </a>
            
            <span className="text-gray-300">|</span>
            
            {/* E-Mail */}
            <a 
              href="mailto:info@jannesmann.de"
              className="flex items-center text-gray-700 hover:text-red-600 transition-colors"
            >
              <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
              <span className="font-medium">info@jannesmann.de</span>
            </a>
          </div>
        </div>

        {/* Desktop Navigation - Rote Leiste */}
        <div className="hidden lg:block bg-red-600 rounded-lg">
          <div className="flex items-center justify-center h-14 px-2">
            <nav className="flex items-center space-x-1">
              {navLinks.map((link, index) => (
                <a
                  key={link.path + index}
                  href={link.path}
                  onClick={(e) => handleNavClick(e, link.path)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap text-base cursor-pointer ${
                    isActive(link.path)
                      ? 'bg-red-700 text-white'
                      : 'text-white hover:bg-red-700'
                  }`}
                >
                  {link.name}
                </a>
              ))}
            </nav>
          </div>
        </div>
        
        {/* Mobile Navigation - 3x3 Grid */}
        <div className="lg:hidden bg-gray-100 border-t border-gray-200 py-2 px-2">
          <div className="grid grid-cols-3 gap-1.5">
            {navLinks.map((link, index) => (
              <div
                key={link.path + index}
                className="bg-white rounded-md border border-gray-200 shadow-sm"
              >
                <a
                  href={link.path}
                  onClick={(e) => handleNavClick(e, link.path)}
                  className="flex flex-col items-center justify-center h-9 cursor-pointer"
                >
                  <span className={`text-[11px] font-semibold transition-colors ${
                    isActive(link.path) ? 'text-gray-900' : 'text-gray-700 hover:text-red-600'
                  }`}>
                    {link.name}
                  </span>
                  {isActive(link.path) && (
                    <span className="w-5 h-0.5 bg-red-600 mt-0.5 rounded-full"></span>
                  )}
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
